import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'
import { Sheet, SheetContent } from './sheet'

/**
 * AppShell — primitive layout top-level
 *
 * Coquille de l'app : sidebar + topbar + content + mobile drawer.
 * Encapsule le layering tonal — calage portails (SidebarRail) : contenu de
 * l'app en blanc (bg-surface-card), sidebar en gris neutre (bg-neutral-50),
 * topbar blanche — et les divisions (border-ink-ghost/30).
 *
 * Philosophie : AppShell ne connait pas la navigation metier. Il expose
 * des slots et l'app injecte les Links / DropdownMenu / UserMenu dedans.
 *
 * Layouts (prop `layout`, defaut 'sidebar') :
 *   - 'sidebar' : sidebar fixe pleine hauteur (brand + nav + footer), topbar
 *                 (left + right) dans le main. Variantes `sidebar` :
 *                 expanded | collapsed | hidden.
 *   - 'rail'    : deux colonnes fixes a gauche, toujours visibles, sans repli.
 *                 Colonne 1 (w-20) = `rail` (sections), colonne 2 (w-52) =
 *                 sidebarHeader + nav. Topbar (left + right) dans le main.
 *
 * Slots :
 *   - brand             : logo + wordmark (sidebar header 'sidebar', ET mobile header + drawer)
 *   - rail              : ('rail') contenu de la colonne 1, rendu tel quel
 *   - nav               : liens de navigation (sidebar desktop + drawer mobile)
 *   - mobileNav         : override du nav dans le drawer mobile. Si undefined, reuse nav.
 *   - sidebarHeader     : ('rail') haut de la colonne 2 (recherche, contexte)
 *   - sidebarFooter     : bas de sidebar desktop ('sidebar') et du drawer mobile (user menu / logout)
 *   - topbarLeft        : gauche topbar desktop
 *   - topbarRight       : droite topbar desktop (theme, avatars, actions)
 *   - mobileTopbarRight : override pour le header mobile. Si undefined, reuse topbarRight.
 *   - children          : contenu de la page (Outlet, routes, pages)
 *
 * Mobile drawer :
 *   - AppShell maintient son propre state (mobileOpen).
 *   - Un burger dans le header mobile l'ouvre.
 *   - Les liens de nav appellent `useAppShell().closeMobileDrawer()` pour le fermer
 *     a la navigation.
 *   - Sur desktop le drawer est invisible, la sidebar est fixe.
 *
 * Usage :
 *   function MainNav() {
 *     const { closeMobileDrawer } = useAppShell()
 *     return (
 *       <button onClick={() => { navigate('/flux'); closeMobileDrawer() }}>Flux</button>
 *     )
 *   }
 *
 *   <AppShell
 *     brand={<Logo />}
 *     nav={<MainNav />}
 *     sidebarFooter={<UserMenu />}
 *     topbarRight={<Search />}
 *   >
 *     <Outlet />
 *   </AppShell>
 */

// ─────────────────────────────────────────────────────────────────────
// Context : permet aux liens de nav metier de fermer le drawer mobile
// ─────────────────────────────────────────────────────────────────────

interface AppShellContextValue {
  /** Ferme le drawer mobile. No-op sur desktop. */
  closeMobileDrawer: () => void
  /** True si le drawer mobile est ouvert. */
  isMobileDrawerOpen: boolean
}

const AppShellContext = React.createContext<AppShellContextValue | null>(null)

/**
 * Hook pour accéder au controle du drawer mobile depuis la nav.
 * Retourne un fallback no-op si utilise en dehors d'AppShell.
 */
export function useAppShell(): AppShellContextValue {
  const ctx = React.useContext(AppShellContext)
  if (ctx) return ctx
  return { closeMobileDrawer: () => {}, isMobileDrawerOpen: false }
}

// ─────────────────────────────────────────────────────────────────────
// Variants
// ─────────────────────────────────────────────────────────────────────

const appShellVariants = cva('min-h-screen bg-surface-card text-ink')

const sidebarVariants = cva(
  'hidden sm:flex fixed inset-y-0 left-0 z-30 flex-col bg-neutral-50 border-r border-ink-ghost/30 overflow-hidden transition-[width]',
  {
    variants: {
      sidebar: {
        expanded:  'w-52',
        collapsed: 'w-16',
        hidden:    'sm:hidden',
      },
    },
    defaultVariants: { sidebar: 'expanded' },
  }
)

const mainVariants = cva('flex flex-col min-h-screen', {
  variants: {
    sidebar: {
      expanded:  'sm:ml-52',
      collapsed: 'sm:ml-16',
      hidden:    'ml-0',
    },
  },
  defaultVariants: { sidebar: 'expanded' },
})

// ─────────────────────────────────────────────────────────────────────
// Composant
// ─────────────────────────────────────────────────────────────────────

export type AppShellLayout = 'sidebar' | 'rail'

export interface AppShellProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof sidebarVariants> {
  /** 'sidebar' (defaut) : sidebar pleine hauteur. 'rail' : rail des sections + colonne des sous-ecrans. */
  layout?: AppShellLayout
  brand?: React.ReactNode
  /** ('rail') Colonne 1, rendue telle quelle. */
  rail?: React.ReactNode
  nav?: React.ReactNode
  /** Override du nav dans le drawer mobile. Si undefined, reuse nav. */
  mobileNav?: React.ReactNode
  /** ('rail') Haut de la colonne 2, au-dessus du nav. */
  sidebarHeader?: React.ReactNode
  sidebarFooter?: React.ReactNode
  topbarLeft?: React.ReactNode
  topbarRight?: React.ReactNode
  /** Override du slot droit en mobile. Si undefined, reuse topbarRight. */
  mobileTopbarRight?: React.ReactNode
  children?: React.ReactNode
}

function BurgerIcon({ open }: { open: boolean }) {
  return (
    <svg
      className="w-5 h-5 text-ink-secondary"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      {open ? (
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
      )}
    </svg>
  )
}

const AppShell = React.forwardRef<HTMLDivElement, AppShellProps>(
  (
    {
      className,
      layout = 'sidebar',
      sidebar,
      brand,
      rail,
      nav,
      mobileNav,
      sidebarHeader,
      sidebarFooter,
      topbarLeft,
      topbarRight,
      mobileTopbarRight,
      children,
      ...props
    },
    ref,
  ) => {
    const [mobileOpen, setMobileOpen] = React.useState(false)
    const closeMobileDrawer = React.useCallback(() => setMobileOpen(false), [])

    const ctxValue = React.useMemo<AppShellContextValue>(
      () => ({ closeMobileDrawer, isMobileDrawerOpen: mobileOpen }),
      [closeMobileDrawer, mobileOpen],
    )

    const isRail = layout === 'rail'
    const showTopbar = Boolean(topbarLeft || topbarRight)
    const drawerNav = mobileNav ?? nav

    const Topbar = showTopbar ? (
      <div className="hidden sm:flex items-center justify-between h-12 px-6 border-b border-ink-ghost/30 bg-surface-card/90 backdrop-blur-sm sticky top-0 z-30 shrink-0">
        <div className="flex items-center gap-3 min-w-0">{topbarLeft}</div>
        <div className="flex items-center gap-3 shrink-0">{topbarRight}</div>
      </div>
    ) : null

    return (
      <AppShellContext.Provider value={ctxValue}>
        <div
          ref={ref}
          className={cn(appShellVariants(), className)}
          {...props}
        >
          {/* ── Mobile header (sm:hidden) ── */}
          <header className="sm:hidden sticky top-0 z-40 flex items-center justify-between h-14 px-4 bg-surface-card/90 backdrop-blur-sm border-b border-ink-ghost/30">
            <div className="flex items-center gap-2.5 min-w-0">
              <button
                type="button"
                onClick={() => setMobileOpen(o => !o)}
                className="p-1 cursor-pointer -ml-1 rounded-md hover:bg-surface-low focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={mobileOpen ? 'Fermer la navigation' : 'Ouvrir la navigation'}
                aria-expanded={mobileOpen}
              >
                <BurgerIcon open={mobileOpen} />
              </button>
              {brand}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {mobileTopbarRight ?? topbarRight}
            </div>
          </header>

          {/* ── Mobile drawer (Sheet) ── */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetContent side="left" className="w-[260px] p-0 flex flex-col bg-neutral-50">
              {brand ? (
                <div className="flex items-center gap-2.5 px-5 h-14 border-b border-ink-ghost/30 shrink-0">
                  {brand}
                </div>
              ) : null}
              {drawerNav ? (
                <nav className="flex-1 overflow-y-auto py-2">{drawerNav}</nav>
              ) : null}
              {sidebarFooter ? (
                <div className="border-t border-ink-ghost/30 shrink-0">{sidebarFooter}</div>
              ) : null}
            </SheetContent>
          </Sheet>

          {isRail ? (
            <>
              {/* ── Colonne 1 : rail des sections (hidden sm:flex) ── */}
              <aside className="hidden sm:flex fixed inset-y-0 left-0 z-40 w-20 flex-col bg-neutral-100 border-r border-ink-ghost/30">
                {rail}
              </aside>

              {/* ── Colonne 2 : sous-ecrans de la section (hidden sm:flex) ── */}
              <aside className="hidden sm:flex fixed inset-y-0 left-20 z-30 w-52 flex-col bg-neutral-50 border-r border-ink-ghost/30">
                {sidebarHeader ? <div className="shrink-0">{sidebarHeader}</div> : null}
                {nav ? (
                  <nav className="flex-1 overflow-y-auto py-2">{nav}</nav>
                ) : null}
              </aside>

              {/* ── Main (topbar + content) ── */}
              <div className="sm:ml-[288px] flex flex-col min-h-screen">
                {Topbar}
                <main className="flex-1">{children}</main>
              </div>
            </>
          ) : (
            <>
              {/* ── Desktop sidebar (hidden sm:flex) ── */}
              <aside className={sidebarVariants({ sidebar })}>
                {brand ? (
                  <div className="flex items-center h-12 px-3 border-b border-ink-ghost/30 shrink-0">
                    {brand}
                  </div>
                ) : null}
                {nav ? (
                  <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2">{nav}</nav>
                ) : null}
                {sidebarFooter ? (
                  <div className="border-t border-ink-ghost/30 shrink-0">{sidebarFooter}</div>
                ) : null}
              </aside>

              {/* ── Main (topbar + content) ── */}
              <div className={mainVariants({ sidebar })}>
                {Topbar}
                <main className="flex-1">{children}</main>
              </div>
            </>
          )}
        </div>
      </AppShellContext.Provider>
    )
  },
)
AppShell.displayName = 'AppShell'

export { AppShell, appShellVariants, sidebarVariants, mainVariants }
